import {
  getFlattenedPathPoints,
  isFreeDrawElement,
  isLinearElement,
} from "@excalidraw/element";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import { paramToColor } from "./color";

import {
  definePreset,
  DIRECTION_SCHEMA,
  DISTANCE_SCHEMA,
  kf,
  samplePath,
  setStatic,
  SLIDE_VECTORS,
  timing,
} from "./helpers";

import type { AnimationPreset } from "./types";

/** Absolute scene coordinates of a path element's flattened centerline,
 * honoring the element's rotation (points are stored unrotated; rendering
 * spins them around the element center). */
export const getAbsolutePathPoints = (
  path: ExcalidrawElement & {
    points: readonly (readonly [number, number])[];
  },
): [number, number][] => {
  const pts = getFlattenedPathPoints(
    path as Parameters<typeof getFlattenedPathPoints>[0],
  );
  // scenes can be edited outside the UI — a missing angle must not NaN-poison
  // every sampled point
  const cos = Math.cos(path.angle ?? 0);
  const sin = Math.sin(path.angle ?? 0);
  const cx = path.width / 2;
  const cy = path.height / 2;
  return pts.map(([x, y]) => {
    const rx = x - cx;
    const ry = y - cy;
    return [
      path.x + cx + rx * cos - ry * sin,
      path.y + cy + rx * sin + ry * cos,
    ];
  });
};

/** Resamples a polyline into evenly spaced offsets from its first point, for
 * smooth motion-path keyframes. Sample density adapts to arc length (short
 * paths stay cheap, long paths stay smooth). */
const resamplePathOffsets = (
  points: readonly (readonly [number, number])[] | readonly number[][],
  samples?: number,
) => {
  const pts = points as readonly [number, number][];
  const lengths: number[] = [0];
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    total += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    lengths.push(total);
  }
  // ~1 keyframe per 6px of path, clamped — 24 keeps short paths smooth,
  // 160 caps the tween-array size for very long scribbles
  samples ??= Math.max(24, Math.min(160, Math.round(total / 6)));
  const dx: number[] = [];
  const dy: number[] = [];
  for (let s = 0; s <= samples; s++) {
    const targetLength = (s / samples) * total;
    let x = pts[pts.length - 1][0];
    let y = pts[pts.length - 1][1];
    for (let i = 1; i < lengths.length; i++) {
      if (targetLength <= lengths[i]) {
        const segment = lengths[i] - lengths[i - 1];
        const f = segment === 0 ? 0 : (targetLength - lengths[i - 1]) / segment;
        x = pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * f;
        y = pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * f;
        break;
      }
    }
    dx.push(x - pts[0][0]);
    dy.push(y - pts[0][1]);
  }
  return { dx, dy };
};

export const isPathElement = (el: ExcalidrawElement) =>
  (isLinearElement(el) || isFreeDrawElement(el)) &&
  !el.isDeleted &&
  el.points.length >= 2;

const RADIUS_SCHEMA = {
  key: "radius",
  label: "Radius",
  min: 5,
  max: 500,
  step: 5,
};

export const spatialPresets: AnimationPreset[] = [
  definePreset(
    "slide",
    "Slide",
    "spatial",
    {
      params: { direction: 1, distance: 100 },
      schema: [DIRECTION_SCHEMA, DISTANCE_SCHEMA],
    },
    (tl, target, _element, cfg) => {
      const [vx, vy] =
        SLIDE_VECTORS[cfg.params.direction ?? 1] ?? SLIDE_VECTORS[1];
      const distance = cfg.params.distance ?? 100;
      tl.to(
        target,
        { dx: vx * distance, dy: vy * distance, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "orbit",
    "Orbit",
    "spatial",
    {
      params: { radius: 60 },
      schema: [RADIUS_SCHEMA],
      duration: 2000,
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      const r = cfg.params.radius ?? 60;
      const path = samplePath((t) => [
        r * Math.sin(2 * Math.PI * t),
        r * (1 - Math.cos(2 * Math.PI * t)),
      ]);
      tl.to(target, kf(cfg, path, "none"), 0);
    },
  ),
  definePreset(
    "figure8",
    "Figure 8",
    "spatial",
    {
      params: { radius: 60 },
      schema: [RADIUS_SCHEMA],
      duration: 2500,
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      const r = cfg.params.radius ?? 60;
      const path = samplePath((t) => [
        r * Math.sin(2 * Math.PI * t),
        (r / 2) * Math.sin(4 * Math.PI * t),
      ]);
      tl.to(target, kf(cfg, path, "none"), 0);
    },
  ),
  definePreset(
    "spiral",
    "Spiral",
    "spatial",
    {
      params: { radius: 100, turns: 2 },
      schema: [
        { key: "radius", label: "Radius", min: 10, max: 500, step: 10 },
        { key: "turns", label: "Turns", min: 1, max: 6, step: 1 },
      ],
      duration: 2500,
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      const r = cfg.params.radius ?? 100;
      const turns = cfg.params.turns ?? 2;
      const path = samplePath(
        (t) => [
          r * t * Math.cos(2 * Math.PI * turns * t),
          r * t * Math.sin(2 * Math.PI * turns * t),
        ],
        48,
      );
      tl.to(target, kf(cfg, path, "none"), 0);
    },
  ),
  definePreset(
    "zigzag",
    "Zigzag",
    "spatial",
    {
      params: { amplitude: 40, distance: 200 },
      schema: [
        { key: "amplitude", label: "Amplitude", min: 5, max: 300, step: 5 },
        DISTANCE_SCHEMA,
      ],
      duration: 1500,
    },
    (tl, target, _element, cfg) => {
      const a = cfg.params.amplitude ?? 40;
      const d = cfg.params.distance ?? 200;
      tl.to(
        target,
        kf(
          cfg,
          {
            dx: [0, a, -a, a, -a, 0],
            dy: [0, d * 0.2, d * 0.4, d * 0.6, d * 0.8, d],
          },
          "none",
        ),
        0,
      );
    },
  ),
  definePreset(
    "flowDots",
    "Flow Dots",
    "spatial",
    {
      params: { count: 3, size: 2, color: -1 },
      schema: [
        { key: "count", label: "Dots", min: 1, max: 12, step: 1 },
        { key: "size", label: "Size", min: 0.5, max: 6, step: 0.5 },
        { key: "color", label: "Dot color", min: -1, max: 0xffffff, step: 1 },
      ],
      duration: 2000,
      repeat: 3,
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      // ByteByteGo-style glowing dots traveling along an arrow/line —
      // apply to a linear element; set repeat -1 for a continuous stream
      // color -1 (default) inherits the element's stroke color ("Auto")
      const color = cfg.params.color ?? -1;
      setStatic(tl, target, {
        flowCount: cfg.params.count ?? 3,
        flowSize: cfg.params.size ?? 2,
        ...(color >= 0 ? { flowColor: paramToColor(color, 0) } : {}),
      });
      tl.fromTo(target, { flowPhase: 0 }, { flowPhase: 1, ...timing(cfg) }, 0);
    },
  ),
  definePreset(
    "followPath",
    "Follow Path",
    "spatial",
    {
      // new clips orient to the path and ride ON it; legacy clips persisted
      // their own params, so changing these defaults doesn't affect them
      params: { rotate: 1, snap: 1 },
      schema: [
        { key: "rotate", label: "Orient to path", min: 0, max: 1, step: 1 },
        { key: "snap", label: "Snap onto path", min: 0, max: 1, step: 1 },
      ],
      duration: 3000,
      easing: "none",
    },
    (tl, target, element, cfg, sceneElements) => {
      // moves the element along a drawn line/arrow/freedraw path: an explicit
      // pathElementId wins, else the nearest path element in the scene;
      // falls back to a small S-curve (also used by the panel preview)
      let path = sceneElements?.find(
        (el) => el.id === cfg.pathElementId && isPathElement(el),
      );
      if (!path && sceneElements) {
        const candidates = sceneElements.filter(
          (el) => el.id !== element.id && isPathElement(el),
        );
        const cx = element.x + element.width / 2;
        const cy = element.y + element.height / 2;
        const centerDist = (el: typeof candidates[number]) =>
          Math.hypot(el.x + el.width / 2 - cx, el.y + el.height / 2 - cy);
        path = candidates.sort((a, b) => centerDist(a) - centerDist(b))[0];
      }
      const absPoints =
        path && isPathElement(path)
          ? getAbsolutePathPoints(
              path as Parameters<typeof getAbsolutePathPoints>[0],
            )
          : null;
      const offsets = absPoints
        ? resamplePathOffsets(absPoints)
        : samplePath((t) => [120 * t, 40 * Math.sin(2 * Math.PI * t)]);
      if (absPoints && (cfg.params.snap ?? 0) >= 1) {
        // ride ON the path: shift every sample so the element's center starts
        // at the path's first point instead of tracing the shape in place
        const baseX = absPoints[0][0] - (element.x + element.width / 2);
        const baseY = absPoints[0][1] - (element.y + element.height / 2);
        for (let i = 0; i < offsets.dx.length; i++) {
          offsets.dx[i] += baseX;
          offsets.dy[i] += baseY;
        }
      }
      const frames: Record<string, number[]> = { ...offsets };
      if ((cfg.params.rotate ?? 0) >= 1) {
        // tangent per sample, unwrapped so the angle never jumps across ±π
        const { dx, dy } = offsets;
        const angles: number[] = [];
        for (let i = 0; i < dx.length; i++) {
          const j = Math.min(i, dx.length - 2);
          let a = Math.atan2(dy[j + 1] - dy[j], dx[j + 1] - dx[j]);
          const prev = angles[angles.length - 1];
          if (prev !== undefined) {
            while (a - prev > Math.PI) {
              a -= 2 * Math.PI;
            }
            while (a - prev < -Math.PI) {
              a += 2 * Math.PI;
            }
          }
          angles.push(a);
        }
        frames.angleDelta = angles;
      }
      tl.to(target, kf(cfg, frames, "none"), 0);
    },
  ),
  definePreset(
    "dashFlow",
    "Dash Flow",
    "spatial",
    {
      params: { distance: 64, mode: 0, dashLen: 8, dashGap: 12 },
      schema: [
        { key: "distance", label: "Distance px", min: -500, max: 500, step: 8 },
        {
          key: "mode",
          label: "Motion",
          min: 0,
          max: 3,
          step: 1,
          options: ["Steady", "Pulse", "Wave", "Stutter"],
        },
        { key: "dashLen", label: "Dash px", min: 2, max: 100, step: 2 },
        { key: "dashGap", label: "Gap px", min: 2, max: 100, step: 2 },
      ],
      duration: 1500,
      repeat: 2,
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      // marching dashes along the stroke — solid strokes get dashed while
      // the animation plays; negative distance reverses the flow. Legacy
      // clips (no dashLen param) keep the renderer's default pattern.
      if (
        cfg.params.dashLen !== undefined ||
        cfg.params.dashGap !== undefined
      ) {
        setStatic(tl, target, {
          dashLen: cfg.params.dashLen ?? 8,
          dashGap: cfg.params.dashGap ?? 12,
        });
      }
      const d = cfg.params.distance ?? 64;
      const mode = Math.round(cfg.params.mode ?? 0);
      if (mode === 1) {
        // pulse: heartbeat surges — bursts of travel with rests between
        tl.to(
          target,
          kf(
            cfg,
            { dashOffset: [0, 0.45, 0.5, 0.95, 1].map((t) => d * t) },
            "power2.inOut",
          ),
          0,
        );
        return;
      }
      const ease =
        mode === 2
          ? "sine.inOut" // wave: ocean-like ebb and flow
          : mode === 3
          ? "steps(9)" // stutter: jerky glitch marching
          : undefined; // steady: the clip's own easing (linear default)
      tl.fromTo(
        target,
        { dashOffset: 0 },
        { dashOffset: d, ...timing(cfg), ...(ease ? { ease } : {}) },
        0,
      );
    },
  ),
  definePreset(
    "flow",
    "Flow",
    "spatial",
    {
      params: { direction: 1, distance: 200 },
      schema: [DIRECTION_SCHEMA, DISTANCE_SCHEMA],
      duration: 1500,
      repeat: 2,
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      // moving-packet look: travels along the direction while fading in/out
      const [vx, vy] =
        SLIDE_VECTORS[cfg.params.direction ?? 1] ?? SLIDE_VECTORS[1];
      const d = cfg.params.distance ?? 200;
      const stops = [0, 0.15, 0.5, 0.85, 1];
      tl.to(
        target,
        kf(
          cfg,
          {
            dx: stops.map((t) => vx * d * t),
            dy: stops.map((t) => vy * d * t),
            opacity: [0, 1, 1, 1, 0],
          },
          "none",
        ),
        0,
      );
    },
  ),
];
